import { t } from '@lingui/core/macro'
import type { jsPDF } from 'jspdf'

import type { WorkOrderEntry } from './workOrder'
import { workOrderDate } from './workOrderDates'
import type { WorkOrderTextRun } from './workOrderHighlights'
import type { WorkOrderField } from './workOrderPresentation'
import { workOrderPlainText } from './workOrderText'

export const SHEET_WIDTH = 612
export const SHEET_HEIGHT = 792
const MARGIN = 40
const BOTTOM = 716
const BODY_WIDTH = SHEET_WIDTH - MARGIN * 2

interface TextOptions {
  field?: string
  size?: number
  bold?: boolean
  muted?: boolean
  x?: number
  width?: number
}

export class WorkOrderSheet {
  y = 43
  runs: WorkOrderTextRun[] = []

  constructor(
    readonly pdf: jsPDF,
    readonly number: string
  ) {}

  header(
    kind: string,
    title: string,
    source: WorkOrderEntry['data']['source'],
    fields = { title: 'title', number: 'title' }
  ) {
    this.pdf.setProperties({
      title: `${this.number} · ${workOrderPlainText(title)}`,
      subject: kind,
      creator: t`Forecast`,
    })
    this.pdf.setFillColor(28, 69, 81)
    this.pdf.rect(40, 22, 28, 3, 'F')
    this.write(`${kind}  /  ${source.system.toUpperCase()} · ${source.site}`, {
      size: 9,
      muted: true,
    })
    this.y += 8
    this.write(title, { size: 22, bold: true, field: fields.title })
    this.y += 5
    this.write(this.number, { size: 11, bold: true, field: fields.number })
  }

  footer(source: WorkOrderEntry['data']['source']) {
    const pageCount = this.pdf.getNumberOfPages()
    const sourceTimes = [
      source.changed_at
        ? `${t`Source updated`}: ${workOrderDate(source.changed_at)}`
        : '',
      source.synced_at
        ? `${t`Imported`}: ${workOrderDate(source.synced_at)}`
        : '',
    ]
      .filter(Boolean)
      .join('  ·  ')
    for (let page = 1; page <= pageCount; page++) {
      this.pdf.setPage(page)
      this.pdf.setDrawColor(217, 225, 229)
      this.pdf.line(40, SHEET_HEIGHT - 57, SHEET_WIDTH - 40, SHEET_HEIGHT - 57)
      this.pdf.setFont('helvetica', 'normal')
      this.pdf.setFontSize(7)
      this.pdf.setTextColor(91, 107, 118)
      const footer: string[] = this.pdf.splitTextToSize(sourceTimes, BODY_WIDTH)
      this.pdf.text(footer.slice(0, 2), 40, SHEET_HEIGHT - 44)
      this.pdf.text(
        t`Rendered from the imported CMMS record.`,
        40,
        SHEET_HEIGHT - 19
      )
      this.pdf.text(
        t`Page ${page} of ${pageCount}`,
        SHEET_WIDTH - 40,
        SHEET_HEIGHT - 19,
        { align: 'right' }
      )
    }
  }

  ensure(height: number) {
    if (this.y + height <= BOTTOM) return
    this.pdf.addPage()
    this.pdf.setFont('helvetica', 'bold')
    this.pdf.setFontSize(9)
    this.pdf.setTextColor(80, 95, 105)
    this.pdf.text(this.number, MARGIN, 30)
    this.pdf.setDrawColor(217, 225, 229)
    this.pdf.line(MARGIN, 39, SHEET_WIDTH - MARGIN, 39)
    this.y = 58
  }

  lines(value: string, size: number, width: number, bold = false): string[] {
    this.pdf.setFont('helvetica', bold ? 'bold' : 'normal')
    this.pdf.setFontSize(size)
    return this.pdf.splitTextToSize(workOrderPlainText(value), width)
  }

  write(value: string, options: TextOptions = {}) {
    if (!value.trim()) return
    const {
      size = 11,
      bold = false,
      muted = false,
      x = MARGIN,
      width = BODY_WIDTH,
      field,
    } = options
    for (const line of this.lines(value, size, width, bold)) {
      const height = line.trim() ? size * 1.4 : size * 0.65
      this.ensure(height)
      this.pdf.setFont('helvetica', bold ? 'bold' : 'normal')
      this.pdf.setFontSize(size)
      this.pdf.setTextColor(muted ? 91 : 30, muted ? 107 : 41, muted ? 118 : 50)
      if (line.trim()) {
        this.pdf.text(line, x, this.y)
        this.runs.push({
          text: line,
          field,
          page: this.pdf.getNumberOfPages(),
          box: {
            left: x / SHEET_WIDTH,
            top: (this.y - size) / SHEET_HEIGHT,
            width: Math.min(width, this.pdf.getTextWidth(line)) / SHEET_WIDTH,
            height: (size * 1.35) / SHEET_HEIGHT,
          },
        })
      }
      this.y += height
    }
  }

  section(label: string, field?: string, nextHeight = 12) {
    this.ensure(46 + nextHeight)
    this.y += 6
    this.pdf.setDrawColor(217, 225, 229)
    this.pdf.line(MARGIN, this.y, SHEET_WIDTH - MARGIN, this.y)
    this.y += 19
    this.write(label, { size: 10, bold: true, field })
    this.y += 7
  }

  status(value: string, field = 'status') {
    this.pdf.setFont('helvetica', 'bold')
    this.pdf.setFontSize(9)
    const width = this.pdf.getTextWidth(value) + 18
    if (width > BODY_WIDTH) {
      this.write(value, { size: 10, field })
      return
    }
    this.ensure(25)
    this.pdf.setFillColor(235, 243, 243)
    this.pdf.roundedRect(MARGIN, this.y - 11, width, 21, 4, 4, 'F')
    this.write(value, { size: 9, bold: true, field, x: MARGIN + 9 })
    this.y += 7
  }

  fieldRowHeight(fields: WorkOrderField[]) {
    const width = (BODY_WIDTH - 24) / 2
    return Math.max(
      0,
      ...fields.map(
        (field) =>
          this.lines(field.label, 8, width, true).length * 11.2 +
          this.lines(field.value, 10, width).length * 14 +
          12
      )
    )
  }

  fields(fields: WorkOrderField[]) {
    const width = (BODY_WIDTH - 24) / 2
    for (let index = 0; index < fields.length; index += 2) {
      const pair = fields.slice(index, index + 2)
      const height = this.fieldRowHeight(pair)
      if (height > BOTTOM - 58) {
        for (const field of pair) {
          this.write(field.label, { size: 8, bold: true, muted: true })
          this.write(field.value, { field: field.id })
          this.y += 8
        }
        continue
      }
      this.ensure(height)
      const top = this.y
      for (const [column, field] of pair.entries()) {
        this.y = top
        const x = MARGIN + column * (width + 24)
        this.write(field.label, { size: 8, bold: true, muted: true, x, width })
        this.y += 2
        this.write(field.value, { size: 10, field: field.id, x, width })
      }
      this.y = top + height
    }
  }

  log(heading: string, title: string, body: string, field: string) {
    const x = MARGIN + 14
    const width = BODY_WIDTH - 14
    const height = [
      { text: heading, size: 8.5, bold: true },
      { text: title, size: 11, bold: Boolean(body) },
      { text: body, size: 11, bold: false },
    ].reduce(
      (total, block) =>
        total +
        (block.text
          ? this.lines(block.text, block.size, width, block.bold).reduce(
              (sum, line) => sum + block.size * (line.trim() ? 1.4 : 0.65),
              0
            )
          : 0),
      14
    )
    this.ensure(height <= BOTTOM - 58 ? height : 65)
    this.pdf.setFillColor(28, 69, 81)
    this.pdf.circle(MARGIN + 2, this.y - 3, 2, 'F')
    if (heading)
      this.write(heading, {
        field,
        size: 8.5,
        bold: true,
        muted: true,
        x,
        width,
      })
    if (title) this.write(title, { field, bold: Boolean(body), x, width })
    if (body) {
      this.y += 2
      this.write(body, { field, x, width })
    }
    this.y += 12
  }

  table(
    headers: string[],
    rows: { label: string; values: WorkOrderField[] }[]
  ) {
    const widths = [160, 124, 124, 124]
    const rowHeights = rows.map((row) =>
      Math.max(
        30,
        this.lines(row.label, 9, widths[0] - 10).length * 12.6 + 8,
        ...row.values.map(
          (field, index) =>
            this.lines(field.value, 9, widths[index + 1] - 10).length * 12.6 + 8
        )
      )
    )
    this.ensure(30 + rowHeights.reduce((sum, height) => sum + height, 0))
    const headerY = this.y
    let x = MARGIN
    for (const [index, header] of headers.entries()) {
      this.y = headerY
      this.write(header, {
        size: 8,
        bold: true,
        muted: true,
        x,
        width: widths[index] - 10,
      })
      x += widths[index]
    }
    this.y = headerY + 30
    for (const [rowIndex, row] of rows.entries()) {
      const height = rowHeights[rowIndex]
      const top = this.y
      this.write(row.label, { size: 9, x: MARGIN, width: widths[0] - 10 })
      x = MARGIN + widths[0]
      for (const [index, field] of row.values.entries()) {
        this.y = top
        const start = this.runs.length
        this.write(field.value, {
          field: field.id,
          size: 9,
          x,
          width: widths[index + 1] - 10,
        })
        for (const run of this.runs.slice(start)) {
          run.contextBox = {
            left: MARGIN / SHEET_WIDTH,
            top: (headerY - 16) / SHEET_HEIGHT,
            width: BODY_WIDTH / SHEET_WIDTH,
            height: (top + height - headerY + 16) / SHEET_HEIGHT,
          }
        }
        x += widths[index + 1]
      }
      this.y = top + height
    }
  }

  reference(label: string, url: string) {
    const start = this.runs.length
    this.write(label, { size: 8.5, muted: true })
    for (const run of this.runs.slice(start)) {
      this.pdf.setPage(run.page)
      this.pdf.link(
        run.box.left * SHEET_WIDTH,
        run.box.top * SHEET_HEIGHT,
        run.box.width * SHEET_WIDTH,
        run.box.height * SHEET_HEIGHT,
        { url }
      )
    }
    this.pdf.setPage(this.pdf.getNumberOfPages())
  }
}
